// Vista BORRADOR de un contrato del CRM: abre una ventana A4 imprimible (el
// usuario la guarda como PDF) ANTES de firmarlo. Replica el texto legal del PDF
// firmado (notificador/src/pdf-contrato-crm.js) pero SIN firma: marca de agua
// "BORRADOR · SIN FIRMA" y un recuadro que aclara que no tiene validez hasta firmarse.
import { apiClient } from '@/lib/api/client';

const CRM = { headers: { 'Content-Profile': 'crm', 'Accept-Profile': 'crm' } };
const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const money = (v: number, mon = 'USD') =>
  `${mon === 'USD' ? '$' : mon + ' '}${(Number(v) || 0).toFixed(2)}${mon === 'USD' ? ' (dólares de los Estados Unidos de América)' : ''}`;
const MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fechaTxt = (d?: string | null) => {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return `${Number(dd)} de ${MES[Number(m) - 1]} de ${y}`;
};
const ORD = ['CUARTA', 'QUINTA', 'SEXTA', 'SÉPTIMA', 'OCTAVA', 'NOVENA', 'DÉCIMA',
  'DÉCIMA PRIMERA', 'DÉCIMA SEGUNDA', 'DÉCIMA TERCERA', 'DÉCIMA CUARTA', 'DÉCIMA QUINTA'];

interface Borrador {
  empresa: { nombre: string; razon_social: string | null; ruc: string | null; direccion: string | null; telefono: string | null; email: string | null; logo: string | null; representante_legal: string | null; representante_legal_id: string | null };
  firmante: string;
  cliente: { nombre: string; identificacion: string | null; direccion: string | null; ciudad: string | null; representante: string | null; representante_identificacion: string | null };
  contrato: { codigo: string | null; titulo: string; objeto: string; clausulas: string | null; monto: number; moneda: string; forma_pago: string | null; fecha_inicio: string; fecha_fin: string | null; estado: string };
}

export async function abrirContratoBorrador(contratoId: string): Promise<void> {
  const d = (await apiClient.post('/rpc/crm_contrato_borrador', { _contrato_id: contratoId }, CRM)).data as Borrador | null;
  if (!d) throw new Error('Contrato no encontrado');
  const e = d.empresa, cl = d.cliente, c = d.contrato, firmante = d.firmante || '';
  const proveedor = e.razon_social || e.nombre;                 // nombre legal en el contrato
  const repLegal = e.representante_legal || firmante;           // representante legal (fallback: firmante)
  const repLegalId = e.representante_legal_id
    ? `, con identificación N.º ${e.representante_legal_id}` : '';

  const clausula = (titulo: string, texto: string) =>
    `<h3>${esc(titulo)}</h3><p class="just">${esc(texto)}</p>`;

  const suscribe = cl.representante
    ? `representada por ${cl.representante}${cl.representante_identificacion ? `, con identificación N.º ${cl.representante_identificacion}` : ''},`
    : '';
  const comparecencia =
    `Comparecen a la celebración del presente contrato, por una parte ${proveedor}, con RUC ${e.ruc || '—'},` +
    `${e.direccion ? ` domiciliada en ${e.direccion},` : ''} representada por ${repLegal}${repLegalId}, a quien en adelante se denominará ` +
    `EL PROVEEDOR; y por otra parte ${cl.nombre}${cl.identificacion ? `, con identificación N.º ${cl.identificacion}` : ''},` +
    ` ${suscribe}${cl.ciudad ? ` domiciliada en ${cl.ciudad},` : ''} a quien en adelante se denominará EL CLIENTE; ` +
    'quienes libre y voluntariamente convienen en celebrar el presente contrato al tenor de las siguientes cláusulas:';

  const partes: string[] = [];
  partes.push(clausula('PRIMERA — OBJETO.', c.objeto || '—'));
  if (Number(c.monto) > 0) {
    partes.push(clausula('SEGUNDA — PRECIO Y FORMA DE PAGO.',
      `EL CLIENTE pagará a EL PROVEEDOR la suma de ${money(c.monto, c.moneda)}` +
      `${c.forma_pago ? `, pagadera de la siguiente forma: ${c.forma_pago}` : ''}. ` +
      'Los valores no incluyen impuestos, salvo pacto expreso en contrario.'));
  }
  let plazo = `El presente contrato rige desde el ${fechaTxt(c.fecha_inicio)}`;
  if (c.fecha_fin) plazo += ` hasta el ${fechaTxt(c.fecha_fin)}`;
  plazo += '.';
  partes.push(clausula(Number(c.monto) > 0 ? 'TERCERA — PLAZO.' : 'SEGUNDA — PLAZO.', plazo));

  const extras = String(c.clausulas || '').split(/\n+/).map((s) => s.trim()).filter(Boolean);
  let idx = Number(c.monto) > 0 ? 0 : -1;
  for (const ex of extras) {
    const nombre = ORD[Math.min(Math.max(idx, 0), ORD.length - 1)];
    idx += 1;
    const m = ex.match(/^([A-ZÁÉÍÓÚÑ\s]{3,40})[:—-]\s*(.+)$/s);
    if (m) partes.push(clausula(`${nombre} — ${m[1].trim().toUpperCase()}.`, m[2].trim()));
    else partes.push(clausula(`${nombre}.`, ex));
  }
  partes.push(clausula('ACEPTACIÓN.',
    'Las partes declaran conocer y aceptar el contenido íntegro de las cláusulas precedentes y suscriben el presente ' +
    'contrato. EL PROVEEDOR lo suscribe mediante FIRMA ELECTRÓNICA con el certificado digital de su representante, ' +
    'al amparo de la Ley de Comercio Electrónico, Firmas Electrónicas y Mensajes de Datos.'));

  const html = `<!doctype html><html><head><meta charset="utf-8">
  <title>Contrato ${esc(c.codigo || '')} — BORRADOR</title>
  <style>
    @page { size: A4; margin: 20mm; }
    body { font-family: Arial, sans-serif; font-size: 11.5px; color: #222; line-height: 1.5; position: relative; }
    h1 { font-size: 16px; text-align: center; margin: 0 0 2px; text-transform: uppercase; }
    .num { text-align: center; color: #555; margin: 0 0 14px; }
    h3 { font-size: 11.5px; margin: 12px 0 3px; }
    p { margin: 0 0 4px; } .just { text-align: justify; }
    .logo { max-height: 54px; max-width: 170px; display: block; margin: 0 auto 6px; }
    .firmas { display: flex; gap: 40px; margin-top: 46px; text-align: center; }
    .firmas > div { flex: 1; }
    .linea { border-top: 1px solid #333; margin: 0 10px 4px; padding-top: 4px; }
    .rol { font-weight: bold; }
    .sub { color: #555; font-size: 10.5px; white-space: pre-line; }
    .aviso { border: 1px solid #c33; color: #a22; border-radius: 6px; padding: 8px 10px; margin-top: 26px; font-size: 10.5px; }
    .marca { position: fixed; top: 42%; left: 6%; transform: rotate(-24deg); font-size: 60px; font-weight: 800;
             color: rgba(200,30,30,0.13); letter-spacing: 4px; pointer-events: none; z-index: 99; }
  </style></head><body>
  <div class="marca">BORRADOR · SIN FIRMA</div>
  ${e.logo ? `<img class="logo" src="${esc(e.logo)}" alt=""/>` : ''}
  <h1>${esc(c.titulo || 'CONTRATO')}</h1>
  <p class="num">${c.codigo ? `N.º ${esc(c.codigo)}` : ''}</p>
  <p class="just">${esc(comparecencia)}</p>
  ${partes.join('')}
  <div class="firmas">
    <div>
      <div class="linea">&nbsp;</div><div class="rol">EL PROVEEDOR</div>
      <div class="sub">${esc(proveedor)}\n${esc(repLegal)}\n(firmará electrónicamente)</div>
    </div>
    <div>
      <div class="linea">&nbsp;</div><div class="rol">EL CLIENTE</div>
      <div class="sub">${esc(cl.nombre)}\n${esc(cl.representante || '')}${cl.identificacion ? `\nIdent. ${esc(cl.identificacion)}` : ''}</div>
    </div>
  </div>
  <div class="aviso"><b>Documento en BORRADOR — sin firma.</b> Es una vista previa para revisión/impresión.
  No tiene validez hasta que se firme electrónicamente con el certificado del representante. Al firmarlo, el
  sistema genera el PDF definitivo con el sello de firma y el QR de verificación pública.</div>
  </body></html>`;

  const win = window.open('', '_blank', 'width=900,height=760');
  if (!win) throw new Error('El navegador bloqueó la ventana emergente');
  win.document.write(html);
  win.document.close();
  setTimeout(() => { try { win.focus(); win.print(); } catch { /* sin impresora */ } }, 300);
}
